import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { Button, SearchableDropdown, DateRangeFilter } from '@/components';
import {
  getOutletOptions,
  getDriverOptions,
  LookupOption,
} from '@/services/api/deliveryLookups';
import { getDeliveryStatusLabel } from './DeliveryStatusBadge';

// Stable module-level fetchers — passing inline arrows as the `fetcher` prop
// would change identity on every render (e.g. typing in the status search),
// re-triggering each AsyncMultiSelect's load effect and refetching needlessly.
const fetchOutlets = (search: string) => getOutletOptions({ search });
const fetchDrivers = (search: string) => getDriverOptions({ search });

// Status grouped into sections (mirrors the prototype's operator board).
const STATUS_GROUPS: { label: string; statuses: string[] }[] = [
  { label: 'Booking', statuses: ['PENDING_PAYMENT', 'QUEUEING', 'ALLOCATING'] },
  { label: 'Pickup', statuses: ['PENDING_PICKUP', 'PICKING_UP', 'PREPARING'] },
  { label: 'Delivery', statuses: ['PENDING_DELIVERY', 'IN_DELIVERY'] },
  {
    label: 'Completion',
    statuses: ['COMPLETED', 'VERIFIED', 'NOT_VERIFIED', 'RETURNED'],
  },
  {
    label: 'Exception',
    statuses: [
      'FAILED',
      'IN_RETURN',
      'PENDING_RETURN',
      'FAILED_IN_RETURN',
      'ON_HOLD',
      'CANCELLED',
      'DISPOSED',
    ],
  },
];

export interface DeliveryFilterValue {
  statuses: string[];
  outlets: LookupOption[]; // selected outlets (value + label) — Recipient
  drivers: LookupOption[]; // selected drivers (value + label) — Mitra
  startDate: string; // created date range
  endDate: string;
}

export const EMPTY_FILTER: DeliveryFilterValue = {
  statuses: [],
  outlets: [],
  drivers: [],
  startDate: '',
  endDate: '',
};

export const countActiveFilters = (v: DeliveryFilterValue): number =>
  v.statuses.length +
  v.outlets.length +
  v.drivers.length +
  (v.startDate || v.endDate ? 1 : 0);

// Multi-select backed by a lookup endpoint. Selected items are passed up as
// full { value, label } objects so chips keep their labels even after the
// panel is closed and reopened (the fetched option list no longer contains
// them). `mode` controls whether search hits the server or filters locally.
const AsyncMultiSelect: React.FC<{
  placeholder: string;
  mode: 'local' | 'server';
  fetcher: (search: string) => Promise<LookupOption[]>;
  selected: LookupOption[];
  onChange: (selected: LookupOption[]) => void;
}> = ({ placeholder, mode, fetcher, selected, onChange }) => {
  const [results, setResults] = useState<LookupOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(
    async (search: string) => {
      setLoading(true);
      try {
        setResults(await fetcher(search));
      } catch {
        // Leave previously-loaded results in place on error.
      } finally {
        setLoading(false);
      }
    },
    [fetcher]
  );

  useEffect(() => {
    load('');
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [load]);

  const handleSearch = (s: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(s), 500);
  };

  // Pin the selected items onto the visible list so their labels resolve.
  const options = useMemo(() => {
    const pinned = selected.filter(
      (s) => !results.some((r) => r.value === s.value)
    );
    return [...results, ...pinned];
  }, [results, selected]);

  const resolve = (id: string): LookupOption =>
    selected.find((o) => o.value === id) ||
    results.find((o) => o.value === id) || { value: id, label: id };

  return (
    <SearchableDropdown
      multiple
      options={options}
      value={selected.map((o) => o.value)}
      onChange={(ids) => onChange(ids.map((id) => resolve(String(id))))}
      onSearchChange={mode === 'server' ? handleSearch : undefined}
      disableLocalFilter={mode === 'server'}
      loading={loading}
      placeholder={placeholder}
    />
  );
};

const sectionLabel =
  'mb-2 text-xs font-semibold uppercase tracking-wide text-text-soft-400';

const toISODate = (d: Date | null) => (d ? dayjs(d).format('YYYY-MM-DD') : '');
const fromISODate = (s: string) => (s ? new Date(s) : null);

interface DeliveryFilterProps {
  value: DeliveryFilterValue;
  onApply: (value: DeliveryFilterValue) => void;
}

const DeliveryFilter: React.FC<DeliveryFilterProps> = ({ value, onApply }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DeliveryFilterValue>(value);
  const [statusQuery, setStatusQuery] = useState('');

  // Re-seed the draft from the applied value each time the drawer opens.
  useEffect(() => {
    if (open) {
      setDraft(value);
      setStatusQuery('');
    }
  }, [open, value]);

  const activeCount = countActiveFilters(value);

  const toggleStatus = (v: string) =>
    setDraft((d) => ({
      ...d,
      statuses: d.statuses.includes(v)
        ? d.statuses.filter((x) => x !== v)
        : [...d.statuses, v],
    }));

  const filteredGroups = useMemo(() => {
    const q = statusQuery.trim().toLowerCase();
    return STATUS_GROUPS.map((g) => ({
      label: g.label,
      items: g.statuses
        .map((s) => ({ value: s, label: getDeliveryStatusLabel(s) }))
        .filter((it) => !q || it.label.toLowerCase().includes(q)),
    })).filter((g) => g.items.length > 0);
  }, [statusQuery]);

  const handleCreatedChange = (range: { from: Date | null; to: Date | null }) =>
    setDraft((d) => ({
      ...d,
      startDate: toISODate(range.from),
      endDate: toISODate(range.to),
    }));

  const apply = () => {
    onApply(draft);
    setOpen(false);
  };

  const reset = () => {
    setDraft(EMPTY_FILTER);
    onApply(EMPTY_FILTER);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center gap-2 rounded-md border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 text-sm text-text-sub-600 hover:bg-bg-weak-50"
      >
        <i className="ri-filter-3-line" />
        Filter
        {activeCount > 0 && (
          <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-static-white">
            {activeCount}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-bg-strong-950/40"
              aria-hidden="true"
              onClick={() => setOpen(false)}
            />

            {/* Right drawer */}
            <div className="fixed right-0 top-0 z-50 flex h-full w-[420px] max-w-[92vw] flex-col bg-bg-white-0 shadow-card-lg">
              {/* Header */}
              <div className="flex shrink-0 items-center justify-between border-b border-stroke-soft-200 px-5 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-text-strong-950">
                    Filter
                  </h2>
                  <p className="text-xs text-text-sub-600">Deliveries</p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-text-soft-400 hover:text-text-sub-600"
                  aria-label="Close filter"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-5 py-5">
                {/* Status */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className={sectionLabel + ' mb-0'}>Status</h4>
                    {draft.statuses.length > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          setDraft((d) => ({ ...d, statuses: [] }))
                        }
                        className="text-xs font-medium text-text-sub-600 hover:text-text-strong-950"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="relative mb-2">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-icon-soft-400" />
                    <input
                      type="text"
                      value={statusQuery}
                      onChange={(e) => setStatusQuery(e.target.value)}
                      placeholder="Search status…"
                      className="w-full rounded-md border border-stroke-sub-300 bg-bg-white-0 py-1.5 pl-9 pr-3 text-sm text-text-strong-950 placeholder:text-text-soft-400 focus:border-primary focus:outline-hidden focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div className="max-h-[220px] space-y-3 overflow-y-auto rounded-lg border border-stroke-soft-200 p-3">
                    {filteredGroups.length === 0 && (
                      <p className="py-2 text-center text-xs text-text-soft-400">
                        No status found
                      </p>
                    )}
                    {filteredGroups.map((g) => (
                      <div key={g.label}>
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-soft-400">
                          {g.label}
                        </div>
                        <div className="space-y-1">
                          {g.items.map((it) => {
                            const checked = draft.statuses.includes(it.value);
                            return (
                              <label
                                key={it.value}
                                className="flex cursor-pointer items-center gap-2.5 py-1"
                              >
                                <span
                                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                                    checked
                                      ? 'border-primary bg-primary'
                                      : 'border-stroke-sub-300 bg-bg-white-0'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={checked}
                                    onChange={() => toggleStatus(it.value)}
                                  />
                                  {checked && (
                                    <i className="ri-check-line text-xs text-static-white" />
                                  )}
                                </span>
                                <span className="text-sm text-text-strong-950">
                                  {it.label}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recipient — `mode="local"` because getOutletOptions pulls the
                    client's own outlets in one page (size 999); searching that
                    in memory beats a request per keystroke. */}
                <div className="mt-5">
                  <h4 className={sectionLabel}>Recipient</h4>
                  <AsyncMultiSelect
                    mode="local"
                    placeholder="Search outlet…"
                    fetcher={fetchOutlets}
                    selected={draft.outlets}
                    onChange={(outlets) => setDraft((d) => ({ ...d, outlets }))}
                  />
                </div>
                {/* Mitra */}
                <div className="mt-5">
                  <h4 className={sectionLabel}>Mitra</h4>
                  <AsyncMultiSelect
                    mode="server"
                    placeholder="Search driver…"
                    fetcher={fetchDrivers}
                    selected={draft.drivers}
                    onChange={(drivers) => setDraft((d) => ({ ...d, drivers }))}
                  />
                </div>
                {/* Created date */}
                <div className="mt-5">
                  <h4 className={sectionLabel}>Created date</h4>
                  <DateRangeFilter
                    openDirection="up"
                    onDateRangeChange={handleCreatedChange}
                    initialRange={{
                      from: fromISODate(draft.startDate),
                      to: fromISODate(draft.endDate),
                    }}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex shrink-0 items-center justify-between border-t border-stroke-soft-200 px-5 py-4">
                <button
                  type="button"
                  onClick={reset}
                  className="text-sm font-medium text-text-sub-600 hover:text-text-strong-950"
                >
                  Reset
                </button>
                <Button variant="primary" size="medium" onClick={apply}>
                  Apply filter
                </Button>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default DeliveryFilter;
